/**
 * Eingangswahl der Capture (Doc 15 §1.2/§6): eigenes Mikrofon per
 * `getUserMedia` (deviceId, Kanal, EC/NS/AGC) oder ein fertiger, vom Aufrufer
 * gehaltener `MediaStream` (z. B. Tab-Audio aus `getDisplayMedia`). Rein,
 * ohne Browser-APIs → testbar.
 */
import type { SourceConfig } from '@voice/protocol';

export type CaptureInputErrorCode = 'no_audio_track' | 'track_ended';

/** Fehler der Eingangswahl; `code` landet im `error`-Ereignis der Capture. */
export class CaptureInputError extends Error {
  readonly code: CaptureInputErrorCode;
  constructor(code: CaptureInputErrorCode, message: string) {
    super(message);
    this.name = 'CaptureInputError';
    this.code = code;
  }
}

export type CaptureInput =
  /** fremder Stream: kein getUserMedia, die Spur gehört dem Aufrufer */
  | { type: 'stream'; track: MediaStreamTrack }
  /** eigenes Mikrofon: Constraints für getUserMedia, die Spuren gehören der Capture */
  | { type: 'device'; constraints: MediaTrackConstraints };

export interface CaptureInputConfig {
  source: Pick<SourceConfig, 'kind' | 'processing' | 'device'>;
  deviceId?: string;
  stream?: MediaStream;
}

/**
 * `stream` schlägt `deviceId`. Vom Stream zählt die erste Audio-Spur; ohne
 * Audio-Spur (beim Teilen „Audio teilen" nicht angehakt) oder mit bereits
 * beendeter Spur (Teilen beendet) gibt es einen `CaptureInputError`.
 * EC/NS/AGC wirken nur auf den Geräte-Zweig; ein fremder Stream kommt so,
 * wie der Aufrufer ihn geholt hat.
 */
export function selectCaptureInput(cfg: CaptureInputConfig): CaptureInput {
  if (cfg.stream) {
    const track = cfg.stream.getAudioTracks()[0];
    if (!track) throw new CaptureInputError('no_audio_track', 'Der gewählte Stream enthält keine Audio-Spur.');
    if (track.readyState === 'ended') throw new CaptureInputError('track_ended', 'Die Audio-Spur des Streams ist bereits beendet.');
    return { type: 'stream', track };
  }
  const src = cfg.source;
  const proc = src.processing ?? { ec: src.kind !== 'room', ns: src.kind !== 'room', agc: src.kind !== 'room' };
  const channel = src.device?.channel ?? null;
  return {
    type: 'device',
    constraints: {
      ...(cfg.deviceId ? { deviceId: { exact: cfg.deviceId } } : {}),
      channelCount: channel !== null ? { ideal: Math.max(2, channel + 1) } : { ideal: 1 },
      echoCancellation: proc.ec,
      noiseSuppression: proc.ns,
      autoGainControl: proc.agc,
    },
  };
}
